<template>
  <ion-page>
    <PageHeaderWithMenu title="Релизы от Лиги" :scrolled="headerScrolled" />
    <ion-content class="main-background programs-page" :fullscreen="true" slot="fixed" :scroll-y="false">
      <ion-segment :value="selectedListSegment" @ionChange="listSegmentChange($event)" mode="ios" class="liga-glass-segment programs-segment">
        <ion-segment-button mode="ios" value="all">
          <ion-label>Все</ion-label>
        </ion-segment-button>
        <ion-segment-button mode="ios" value="favourites">
          <ion-label>Избранное</ion-label>
        </ion-segment-button>
      </ion-segment>

      <ion-content class="bg-transparent programs-content" v-show="selectedListSegment == 'all'" @ionScroll="onContentScroll">
        <div class="programs-grid-page ion-padding-horizontal">
          <div class="programs-grid">
            <div
              v-for="(program, programIdx) in programs"
              :key="program.id ?? programIdx"
              class="program-tile"
              role="button"
              tabindex="0"
              @click="openProgramReleases(program)"
              @keyup.enter="openProgramReleases(program)"
            >
              <div class="program-tile-inner">
                <img
                  class="program-tile-logo"
                  :src="programLogos[program.shortName] || getProgramImage(program.shortName)"
                  :alt="program.programName || 'Программа'"
                  @error="onProgramLogoError(program.shortName)"
                />
              </div>
            </div>
          </div>
        </div>
        <ion-item class="bg-transparent fake-item" lines="none"></ion-item>
      </ion-content>

      <ion-content class="bg-transparent programs-content programs-content-favourites" v-show="selectedListSegment == 'favourites'" @ionScroll="onContentScroll">
        <div class="programs-cards-page ion-padding-horizontal">
          <template v-for="(program, programIdx) in programs" :key="'fav-p-' + (program.id ?? programIdx)">
            <section v-if="hasAnyFavourite(program)" class="program-section">
              <div class="program-hero program-hero--compact">
                <div class="program-hero-inner program-hero-inner--compact">
                  <div class="program-hero-logo program-hero-logo--compact">
                    <img
                      :src="programLogos[program.shortName] || getProgramImage(program.shortName)"
                      alt=""
                      @error="onProgramLogoError(program.shortName)"
                    />
                  </div>
                </div>
              </div>
              <div class="releases-row">
                <div v-for="release in program.fullReleases" :key="'fav-r-' + release.id">
                  <template v-if="isFavourite(program, release)">
                    <div class="release-card-wrap">
                      <button
                        type="button"
                        class="release-card release-card--fav"
                        :class="{ 'release-card--text-title': !releaseTitleIsNumericOnly(release.releaseName) }"
                        :disabled="payingKey === payKey(program, release)"
                        @click="onReleaseCardTap(program, release)"
                      >
                        <div
                          class="release-card-main"
                          :class="{
                            'release-card-main--numeric': releaseTitleIsNumericOnly(release.releaseName),
                            'release-card-main--text': !releaseTitleIsNumericOnly(release.releaseName),
                          }"
                        >
                          <template v-if="releaseTitleIsNumericOnly(release.releaseName)">
                            <div class="release-card-num-line">
                              <span class="release-card-num">{{ releaseTitleTrimmed(release.releaseName) }}</span>
                              <ion-icon
                                v-if="release.canPurchase && !releaseCatalogCanOpen(release)"
                                :icon="lockClosedOutline"
                                class="release-lock release-lock--num"
                                aria-hidden="true"
                              ></ion-icon>
                            </div>
                          </template>
                          <template v-else>
                            <div class="release-card-text-block">
                              <span class="release-card-text-title">{{ release.releaseName }}</span>
                              <ion-icon
                                v-if="release.canPurchase && !releaseCatalogCanOpen(release)"
                                :icon="lockClosedOutline"
                                class="release-lock release-lock--text"
                                aria-hidden="true"
                              ></ion-icon>
                            </div>
                          </template>
                        </div>
                        <div
                          v-if="release.canPurchase && release.priceKopecks && !releaseCatalogCanOpen(release)"
                          class="release-card-footer"
                        >
                          <span class="release-card-price-pill">{{ (release.priceKopecks / 100).toFixed(0) }} ₽</span>
                          <span class="release-card-buy-label">Купить</span>
                        </div>
                        <div v-else-if="!releaseCatalogCanOpen(release)" class="release-card-footer release-card-footer--muted">
                          <span class="release-card-muted">Нет доступа</span>
                        </div>
                        <ion-spinner v-if="payingKey === payKey(program, release)" name="crescent" class="release-card-spinner"></ion-spinner>
                      </button>
                      <div class="release-card-float-actions">
                        <ion-button fill="clear" size="small" class="release-float-btn" @click.stop="toggleFavourite(program, release)">
                          <ion-icon slot="icon-only" :icon="star"></ion-icon>
                        </ion-button>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </section>
          </template>
        </div>
        <ion-item class="bg-transparent fake-item" lines="none"></ion-item>
      </ion-content>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { FavouriteRelease } from "@/model/favouriterelease.model";
import { GlobalContext } from "@/model/globalcontext.model";
import { Program } from "@/model/program.model";
import { ProgramRelease } from "@/model/programrelease.model";
import FilesystemCustomUtils from "@/services/filesystem.custom.utils";
import LogoService, { bundledProgramImageUrl } from "@/services/logo.service";
import { loadFavouriteReleasesFromLocalStorage, saveFavouriteReleasesToLocalStorage } from "@/utils/favourite-releases-local";
import { isNetworkConnected } from "@/utils/network-status";
import { releaseTitleIsNumericOnly, releaseTitleTrimmed } from "@/utils/release-card-display";
import { releaseEffectiveHasAccess } from "@/utils/release-catalog-privilege";
import releasesService from "@/services/releases.service";
import UnicodeUtils from "@/services/unicode.utils";
import store from "@/store/index";
import { CapacitorHttp } from "@capacitor/core";
import { Directory, Encoding, Filesystem } from "@capacitor/filesystem";
import { Preferences } from "@capacitor/preferences";
import PageHeaderWithMenu from "@/components/PageHeaderWithMenu.vue";
import {
    IonButton,
    IonContent,
    IonIcon,
    IonItem,
    IonLabel,
    IonPage,
    IonSegment,
    IonSegmentButton,
    IonSpinner,
    alertController,
    isPlatform,
    toastController
} from "@ionic/vue";
import { lockClosedOutline, star, starOutline } from "ionicons/icons";
import { defineComponent, inject } from "vue";
export default defineComponent({
  name: "Programs",
  components: {
    PageHeaderWithMenu,
    IonContent,
    IonPage,
    IonItem,
    IonLabel,
    IonButton,
    IonIcon,
    IonSpinner,
    IonSegment,
    IonSegmentButton,
  },
  setup() {
    const filesystemCustomUtils = new FilesystemCustomUtils();
    const globalContext = inject("globalContext") as GlobalContext;
    const isMobile = globalContext.isMobile;
    const FAVOURITES_DIRECTORY = "favourites";
    const unicodeUtils = new UnicodeUtils();
    const logoService = new LogoService();

    const getProgramImage = (shortName: string | undefined): string => bundledProgramImageUrl(shortName);

    return {
      globalContext,
      filesystemCustomUtils,
      isMobile,
      starOutline,
      star,
      FAVOURITES_DIRECTORY,
      unicodeUtils,
      lockClosedOutline,
      getProgramImage,
      logoService,
      releaseTitleIsNumericOnly,
      releaseTitleTrimmed,
    };
  },
  data() {
    return {
      programs: Array<Program>(),
      favourites: Array<FavouriteRelease>(),
      selectedListSegment: "all",
      programLogos: {} as Record<string, string>,
      payingKey: null as string | null,
      headerScrolled: false,
    };
  },

  ionViewWillEnter() {
    this.getPrograms();
    this.getAllFavourites();
  },

  methods: {
    onContentScroll(ev: CustomEvent) {
      const t = (ev.detail as { scrollTop?: number }).scrollTop || 0;
      this.headerScrolled = t > 8;
    },
    returnBack(componentName?: string) {
      this.$router.replace({
        name: componentName ? componentName : "Programs",
      });
    },
    closeSliding() {
      const itemSliding = document.getElementsByName(`itemSliding-${this.selectedListSegment}`);
      for (let i = 0; i < itemSliding.length; i++) {
        (itemSliding[i] as any).closeOpened();
      }
    },

    listSegmentChange(event: CustomEvent): void {
      this.selectedListSegment = event.detail.value;
      this.closeSliding();
    },

    async openProgramReleases(program: Program) {
      if (this.globalContext.offlineMode) {
        const online = await isNetworkConnected();
        if (online) {
          this.globalContext.offlineMode = false;
        } else {
          await this.showAlert("Офлайн", "Список релизов программы доступен только онлайн.");
          return;
        }
      }
      const sn = program.shortName?.trim();
      if (!sn) {
        await this.showAlert("Нельзя открыть", "Не задан код программы.");
        return;
      }
      this.$router.push({ name: "ProgramReleases", params: { shortName: sn } });
    },

    async showAlert(alertTitle: string, alertMessage: string) {
      const alert = await alertController.create({
        header: alertTitle,
        message: alertMessage,
        buttons: ["OK"],
      });

      await alert.present();
    },

    async loadProgramsFromLocalCache() {
      try {
        const programsFile = await Filesystem.readFile({
          path: `${this.filesystemCustomUtils.PLAYLISTS_DIRECTORY}/${this.filesystemCustomUtils.PROGRAMS_PATH}`,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
        this.programs = [];
        let parsedData;
        try {
          parsedData = JSON.parse(typeof programsFile.data === "string" ? programsFile.data : "[]");
        } catch (e) {
          console.error("Local programs parse error", e);
          parsedData = [];
        }
        if (Array.isArray(parsedData)) {
          this.programs = parsedData;
        } else if (parsedData && typeof parsedData === "object") {
          this.programs = [parsedData];
        }
        this.programs.forEach((p) => {
          if (!p) return;
          p.fullReleases = Array.isArray(p.fullReleases) ? p.fullReleases : [];
          try {
            if (p.fullReleases.length) {
              p.fullReleases.sort(function (a, b) {
                const keyA =
                  ((a && a.releaseName && a.releaseName.length) || 0) > 1
                    ? (a.releaseName || "")
                    : a ? `0${a.releaseName}` : "0";
                const keyB =
                  ((b && b.releaseName && b.releaseName.length) || 0) > 1
                    ? (b.releaseName || "")
                    : b ? `0${b.releaseName}` : "0";
                if (keyA < keyB) return 1;
                if (keyA > keyB) return -1;
                return 0;
              });
            }
          } catch (err) {
            console.warn("Sort releases skipped (offline)", err);
          }
        });
        this.loadProgramLogos();
      } catch (err) {
        console.error("Failed to load local programs list", err);
      }
    },

    async getPrograms() {
      // Stale offlineMode при живой сети — пробуем API, а не только кэш.
      if (this.globalContext.offlineMode) {
        const online = await isNetworkConnected();
        if (online) {
          this.globalContext.offlineMode = false;
        } else {
          await this.loadProgramsFromLocalCache();
          return;
        }
      }

      const tokenQuery = await Preferences.get({ key: "jwt" });
      const token = tokenQuery.value;

      const options = {
        url: `${process.env.VUE_APP_API_URL}api/programs-with-releases`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      };

      await CapacitorHttp.get(options)
        .then((response) => {
          let data = response.data;
          if (typeof data === "string") {
            try {
              data = JSON.parse(data);
            } catch (e) {
              console.error("Programs response parse error", e);
              data = [];
            }
          }
          if (!Array.isArray(data)) {
            data = (data && (data.programs || data.data || data.list)) || [];
            if (!Array.isArray(data)) data = [];
          }
          this.programs = data;
          this.globalContext.offlineMode = false;

          this.programs.forEach((p) => {
            if (!p) return;
            p.fullReleases = Array.isArray(p.fullReleases) ? p.fullReleases : [];
            try {
              if (p.fullReleases.length) {
                p.fullReleases.sort(function (a, b) {
                  const keyA =
                    ((a && a.releaseName && a.releaseName.length) || 0) > 1
                      ? (a.releaseName || "")
                      : a ? `0${a.releaseName}` : "0";
                  const keyB =
                    ((b && b.releaseName && b.releaseName.length) || 0) > 1
                      ? (b.releaseName || "")
                      : b ? `0${b.releaseName}` : "0";
                  if (keyA < keyB) return 1;
                  if (keyA > keyB) return -1;
                  return 0;
                });
              }
            } catch (err) {
              console.warn("Sort releases skipped", err);
            }
          });
          if (this.isMobile) {
            try {
              this.filesystemCustomUtils.storeFile(
                btoa(JSON.stringify(this.programs)),
                this.filesystemCustomUtils.PROGRAMS_PATH,
                this.filesystemCustomUtils.PLAYLISTS_DIRECTORY
              );
            } catch (err) {
              console.warn("Store programs cache failed", err);
            }
          }
          this.loadProgramLogos();
        })
        .catch(async (ex) => {
          console.error(ex);
          const online = await isNetworkConnected();
          if (!online) {
            this.globalContext.offlineMode = true;
            await this.loadProgramsFromLocalCache();
            return;
          }
          this.$router.replace("/badconnection");
        });
    },

    async loadProgramLogos() {
      if (!Array.isArray(this.programs)) return;
      const loadPromises = this.programs.map((program: Program) => {
        if (program && program.shortName) {
          const shortName = program.shortName;
          return this.logoService
            .getProgramLogo(shortName)
            .then((logoUrl) => {
              if (shortName) this.programLogos[shortName] = logoUrl;
            })
            .catch((error) => {
              console.warn("Logo load failed for " + program.shortName, error);
            });
        }
        return Promise.resolve();
      });
      await Promise.all(loadPromises);
    },

    onProgramLogoError(shortName: string | undefined) {
      if (!shortName) return;
      this.logoService.forgetProgramLogo(shortName);
      this.programLogos[shortName] = bundledProgramImageUrl(shortName);
    },

    getAllFavourites() {
      if (isPlatform("ios") || isPlatform("android")) {
        this.favourites = new Array<FavouriteRelease>();
        Filesystem.mkdir({
          path: this.FAVOURITES_DIRECTORY,
          directory: Directory.Data,
          recursive: true,
        })
        .catch(() => {
          // directory exists
        })
        .finally(() => {
          Filesystem.readdir({
            path: this.FAVOURITES_DIRECTORY,
            directory: Directory.Data,
          })
            .then((favouritesDirs) => {
              for (let i = 0; i < favouritesDirs.files.length; i++) {
                Filesystem.readFile({
                  path: `${this.FAVOURITES_DIRECTORY}/${favouritesDirs.files[i].name}`,
                  directory: Directory.Data,
                  encoding: Encoding.UTF8,
                })
                  .then((res) => {
                    const favourite: FavouriteRelease = JSON.parse(
                      typeof res.data === 'string' ? res.data : ''
                    );
                    this.favourites.push(favourite);
                  })
                  .catch((err) => {
                    console.error("Failed to load favourite", err);
                  });
              }
            })
            .catch(() => {
              // not exists
            });
        })
      } else {
        this.favourites = loadFavouriteReleasesFromLocalStorage();
      }
    },

    hasAnyFavourite(program: Program) {
      const list = program.fullReleases;
      if (!Array.isArray(list)) return false;
      return list.some((r) => this.isFavourite(program, r));
    },

    isFavourite(program: Program, release: ProgramRelease) {
      const id = `${program.shortName}-${release.releaseName}`
      const favourite = this.favourites.find((f) => {
        return f.id === id;
      });

      if (undefined === favourite || null === favourite) return false;

      return favourite.favourite || false;
    },

    addToFavourites(program: Program, release: ProgramRelease, isFavourite: boolean) {
      const id = `${program.shortName}-${release.releaseName}`;
      let isNew = false;
      let favourite = this.favourites.find((f) => {
        return f.id === id;
      });

      if (undefined === favourite || null === favourite) {
        favourite = new FavouriteRelease(id);
        isNew = true;
      }

      favourite.favourite = isFavourite;

      if (isPlatform("ios") || isPlatform("android")) {
        this.filesystemCustomUtils.storeFile(
          this.unicodeUtils.toBase64(JSON.stringify(favourite)),
          `${id}-favourite.json`,
          `${this.FAVOURITES_DIRECTORY}`
        );
        if (isNew) {
          this.favourites.push(favourite);
        }
      } else {
        if (isNew) {
          this.favourites.push(favourite);
        }
        saveFavouriteReleasesToLocalStorage(this.favourites);
      }
      this.closeSliding();
    },

    payKey(program: Program, release: ProgramRelease) {
      return `${program.id}-${release.id}`;
    },

    toggleFavourite(program: Program, release: ProgramRelease) {
      this.addToFavourites(program, release, !this.isFavourite(program, release));
    },

    isValidEmail(s: string | null | undefined): boolean {
      if (s == null || typeof s !== "string") return false;
      const t = s.trim();
      const at = t.indexOf("@");
      return at > 0 && at < t.length - 1 && t.indexOf(".", at + 1) > at + 1;
    },

    releaseCatalogCanOpen(release: ProgramRelease): boolean {
      return releaseEffectiveHasAccess(release, store.getters.account);
    },

    onReleaseCardTap(program: Program, release: ProgramRelease) {
      if (this.releaseCatalogCanOpen(release)) {
        this.openRelease(program, release);
        return;
      }
      if (release.canPurchase) {
        this.goPayRelease(program, release);
        return;
      }
      this.showAlert("Нет доступа", "Этот релиз недоступен для просмотра.");
    },

    async goPayRelease(program: Program, release: ProgramRelease) {
      if (!release.id) return;
      const acct = store.getters.account as { email?: string } | null | undefined;
      const userEmail = acct?.email?.trim();
      if (!this.isValidEmail(userEmail)) {
        const alert = await alertController.create({
          header: "Email для чека",
          message: "Укажите электронную почту, на которую будет направлен чек после оплаты.",
          inputs: [{ name: "email", type: "email", placeholder: "example@mail.ru", attributes: { required: true } }],
          cssClass: "liga-email-receipt-alert",
          buttons: [
            { text: "Отмена", role: "cancel" },
            {
              text: "Оплатить",
              handler: (data: { email?: string }) => {
                const entered = data?.email?.trim();
                if (!this.isValidEmail(entered)) {
                  toastController
                    .create({ message: "Укажите корректный email для чека.", color: "warning", duration: 4000 })
                    .then((t) => t.present());
                  return false;
                }
                const emailForReceipt = entered as string;
                this.executeReleasePurchase(program, release, emailForReceipt);
                return true;
              },
            },
          ],
        });
        await alert.present();
        return;
      }
      const emailForReceipt = userEmail as string;
      const toast = await toastController.create({
        message: `Чек будет направлен на ${emailForReceipt}`,
        color: "primary",
        duration: 2500,
      });
      await toast.present();
      await this.executeReleasePurchase(program, release, emailForReceipt);
    },

    async executeReleasePurchase(program: Program, release: ProgramRelease, customerEmail: string) {
      if (!release.id) return;
      const key = this.payKey(program, release);
      this.payingKey = key;
      try {
        const { redirectUrl } = await releasesService.createReleasePurchase(release.id, customerEmail);
        if (redirectUrl) {
          if (this.isMobile) {
            window.open(redirectUrl, "_system");
          } else {
            window.location.href = redirectUrl;
          }
        }
      } catch (e: any) {
        const data = e?.response?.data ?? e?.data;
        const msg =
          (typeof data?.message === "string" && !String(data.message).startsWith("error.") ? data.message : null) ||
          e?.message ||
          "Ошибка оплаты";
        const toast = await toastController.create({ message: msg, color: "warning", duration: 5000 });
        await toast.present();
      } finally {
        this.payingKey = null;
      }
    },

    async openRelease(program: Program, release: ProgramRelease) {
      if (!this.releaseCatalogCanOpen(release)) {
        this.showAlert("Нет доступа", "Сначала оформите покупку релиза.");
        return;
      }

      await Preferences.set({
        key: "selectedProgram",
        value: program.programName || "",
      });

      Preferences.set({
        key: "releaseMeta",
        value: JSON.stringify({
          programName: program.programName,
          programShortName: program.shortName,
          release: release.releaseName,
          releaseVisible: String(release.visible),
          releaseId: String(release.id),
          contentSource: release.contentSource || 'GOOGLE',
        }),
      }).then(() => {
        this.$router.push({
          name: "ReleasePage",
        });
      });
    },
  },
});
</script>

<style scoped>
.programs-page {
  --padding-bottom: 0;
}
.programs-segment {
  margin: 4px 12px 8px;
}
.programs-grid-page {
  padding-bottom: 2rem;
  padding-top: 4px;
}
.programs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
  gap: 12px;
}
.program-tile {
  border-radius: 16px;
  border: 1px solid var(--liga-program-tile-border, rgba(255, 255, 255, 0.1));
  background: var(--liga-program-tile-bg, rgba(255, 255, 255, 0.06));
  box-shadow: var(--liga-program-tile-shadow, 0 6px 18px rgba(0, 0, 0, 0.22));
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease, background 0.12s ease;
}
.program-tile:focus {
  outline: none;
  border-color: var(--liga-program-tile-border-focus, rgba(255, 255, 255, 0.22));
  background: var(--liga-program-tile-bg-focus, rgba(255, 255, 255, 0.09));
}
.program-tile:active {
  transform: scale(0.98);
}
.program-tile-inner {
  position: relative;
  min-height: 124px;
  padding: 16px 12px 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.program-tile-logo {
  max-width: 100%;
  max-height: 80px;
  width: auto;
  object-fit: contain;
  filter: drop-shadow(0 3px 10px rgba(0, 0, 0, 0.3));
}
.programs-cards-page {
  padding-bottom: 2rem;
}
.program-section {
  margin-bottom: 1.25rem;
  padding: 10px;
  border-radius: 16px;
  background: var(--liga-program-section-bg, rgba(255, 255, 255, 0.04));
  border: 1px solid var(--liga-program-section-border, rgba(255, 255, 255, 0.08));
  backdrop-filter: var(--liga-float-glass-blur, blur(18px));
  -webkit-backdrop-filter: var(--liga-float-glass-blur, blur(18px));
  box-shadow: var(--liga-program-section-shadow, none);
}
.program-hero {
  position: relative;
  padding: 0.2rem 0 0.75rem;
  color: var(--liga-chrome-text, #fff);
}
.program-hero-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 72px;
  padding: 0 8px;
}
.program-hero-inner--compact {
  min-height: 48px;
  padding-right: 0;
}
.program-hero--compact {
  padding-bottom: 0.4rem;
}
.program-hero-logo {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 6px 14px;
  border-radius: 12px;
  background: var(--liga-program-hero-logo-bg, transparent);
}
.program-hero-logo img {
  max-width: 200px;
  max-height: 88px;
  width: auto;
  object-fit: contain;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.35));
}
.program-hero-logo--compact img {
  max-height: 52px;
  max-width: 160px;
}
.releases-row {
  display: flex;
  flex-wrap: nowrap;
  gap: 12px;
  overflow-x: auto;
  padding: 4px 2px 10px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
}
.release-card-wrap {
  position: relative;
  flex: 0 0 auto;
  width: 158px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
.release-card {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 118px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: linear-gradient(
    160deg,
    rgba(118, 110, 168, 0.5) 0%,
    rgba(72, 68, 118, 0.78) 48%,
    rgba(52, 48, 88, 0.88) 100%
  );
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.32),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  color: #fff;
  text-align: center;
  padding: 12px 10px 12px;
  font-family: "Montserrat", system-ui, sans-serif;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition:
    transform 0.12s ease,
    box-shadow 0.12s ease,
    border-color 0.12s ease;
}
.release-card--text-title {
  text-align: center;
}
.release-card:not(:disabled):hover {
  border-color: rgba(255, 255, 255, 0.22);
  box-shadow:
    0 10px 28px rgba(0, 0, 0, 0.38),
    inset 0 1px 0 rgba(255, 255, 255, 0.14);
}
.release-card:not(:disabled):active {
  transform: scale(0.98);
}
.release-card:disabled {
  opacity: 0.72;
}
.release-card-main {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 3.25rem;
}
.release-card-main--numeric {
  min-height: 4rem;
  padding: 2px 6px 0;
}
.release-card-num-line {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
  max-width: 100%;
}
.release-card-num {
  font-size: clamp(2rem, 14vw, 3.35rem);
  font-weight: 800;
  line-height: 0.95;
  letter-spacing: -0.04em;
  font-variant-numeric: tabular-nums;
  color: rgba(255, 255, 255, 0.98);
  text-shadow: 0 2px 14px rgba(0, 0, 0, 0.38);
}
.release-card-main--text {
  min-height: 3.1rem;
  padding: 2px 30px 2px 8px;
  align-items: center;
}
.release-card-text-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  max-height: 5.2em;
}
.release-card-text-title {
  font-size: clamp(0.7rem, 3.4vw, 0.92rem);
  font-weight: 700;
  line-height: 1.22;
  text-align: center;
  word-break: break-word;
  overflow-wrap: anywhere;
  hyphens: auto;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: rgba(255, 255, 255, 0.96);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.28);
}
.release-lock {
  color: rgba(255, 213, 128, 0.95);
  flex-shrink: 0;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.35));
}
.release-lock--num {
  font-size: 1.05rem;
}
.release-lock--text {
  font-size: 1.05rem;
}
.release-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}
.release-card-footer--muted {
  border-top-color: rgba(255, 255, 255, 0.08);
  justify-content: center;
}
.release-card-price-pill {
  font-size: 0.8rem;
  font-weight: 800;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 149, 71, 0.22);
  color: #ffd4b8;
  border: 1px solid rgba(255, 149, 71, 0.42);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}
.release-card-buy-label {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
}
.release-card-muted {
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.48);
  letter-spacing: 0.02em;
}
.release-card-spinner {
  position: absolute;
  left: 50%;
  bottom: 10px;
  transform: translateX(-50%);
  width: 22px;
  height: 22px;
  color: #fff;
}
.release-card-float-actions {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 4;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 0;
  pointer-events: none;
}
.release-card-float-actions .release-float-btn {
  pointer-events: auto;
  margin: 0;
  height: 34px;
  min-width: 34px;
  --padding-start: 0;
  --padding-end: 0;
  --border-radius: 50%;
  --background: rgba(18, 14, 42, 0.42);
  --background-activated: rgba(36, 30, 72, 0.62);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}
.release-card-float-actions .release-float-btn ion-icon {
  font-size: 1.12rem;
}
h1 {
  letter-spacing: 0.08em;
}

.program-main {
  background-color: black;
  opacity: 0.9;
}

.program-item {
  --background: rgba(67, 67, 67, 0.7);
  color: white;
  --color-hover: white;
  font-weight: bold;
  --min-height: 100px;
  margin-bottom: 5px;
  border-bottom: 1px solid rgba(67, 67, 67, .5);
}

.program-img-wrapper {
  width: 100%;
  max-height: 100%;
  display: flex;
  justify-content: center;
}

.program-img-wrapper img {
  scale: 0.7;
  object-fit: scale-down;
}

.release-item {
  --background: rgba(134, 134, 134, 0.5);
  color: white !important;
  --min-height: 5em;
  font-weight: bold;
  border-bottom: 1px solid rgba(67, 67, 67, .5);
}

.release-favourite-item {
  border: unset !important;
  margin-bottom: 5px !important;
}

.release-item ion-label {
  font-family: 'Montserrat';
  color: white;
}

.programs-content-favourites ion-item-option {
  margin-bottom: 5px !important;
}

.release-add-item {
  --min-height: 60px;
  border-bottom: 1px solid rgba(67, 67, 67, .5);
}

.release-option {
  --background: var(--ion-color-orange);
}

.release-option ion-icon {
  color: white;
  scale: 1.5;
}

.release-container {
  position: relative;
}

.download-progress {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  z-index: 100;
  background: var(--ion-color-orange);
  opacity: 0.6;
}

ion-item::part(detail-icon) {
  color: white;
}

/* 
  ##Device = Desktops
  ##Screen = 1281px to higher resolution desktops
*/

@media (min-width: 1281px) {
  .program-img-wrapper img {
    width: 30%;
  }
}

/* 
  ##Device = Laptops, Desktops
  ##Screen = B/w 1025px to 1280px
*/

@media (min-width: 1025px) and (max-width: 1280px) {
  .program-img-wrapper img {
    width: 30%;
  }
}

/* 
  ##Device = Tablets, Ipads (portrait)
  ##Screen = B/w 768px to 1024px
*/

@media (min-width: 768px) and (max-width: 1024px) {
  .program-img-wrapper img {
    width: 30%;
  }
}

/* 
  ##Device = Tablets, Ipads (landscape)
  ##Screen = B/w 768px to 1024px
*/
@media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
  .program-img-wrapper img {
    width: 30%;
  }
}
</style>